import { useEffect, useState } from 'react';

/**
 * 토큰 점검 화면 (`/_tokens`).
 *
 * ★★ **뼈대가 선 것을 눈으로 확인하는 자리다.** 이 프로젝트에서 반복해 나온
 * 실패 유형이 「모든 자동 검증을 통과하면서 화면만 거짓말하는 것」이라
 * (CLAUDE.md), 토큰도 빌드가 통과한다고 맞는 것이 아니다 — 폰트가 안 붙거나
 * 변수 이름이 어긋나면 **조용히 기본값으로** 그려진다.
 *
 * ⚠️ 운영 화면이 아니다. `_` 접두사가 그 표시다.
 */

const SWATCHES: Array<{ label: string; cls: string; note?: string }> = [
  { label: 'primary', cls: 'bg-primary', note: '인디고 — 강조' },
  { label: 'secondary', cls: 'bg-secondary' },
  { label: 'background', cls: 'bg-background' },
  { label: 'card', cls: 'bg-card' },
  { label: 'border', cls: 'bg-border' },
  { label: 'success', cls: 'bg-success', note: 'KPI 정상' },
  { label: 'warning', cls: 'bg-warning', note: 'KPI 경고' },
  { label: 'error', cls: 'bg-error', note: 'KPI 위반' },
  { label: 'info', cls: 'bg-info' },
  { label: 'sidebar', cls: 'bg-sidebar' },
  { label: 'sidebar-accent', cls: 'bg-sidebar-accent' },
];

const CHARTS = ['chart-1', 'chart-2', 'chart-3', 'chart-4', 'chart-5'];

const TYPE: Array<{ cls: string; label: string }> = [
  { cls: 'text-display-lg', label: 'display-lg 40/500' },
  { cls: 'text-display-md', label: 'display-md 34/500' },
  { cls: 'text-display-sm', label: 'display-sm 28/500' },
  { cls: 'text-headline-md', label: 'headline-md 30/500' },
  { cls: 'text-headline-sm', label: 'headline-sm 24/500' },
  { cls: 'text-title-lg', label: 'title-lg 19/500' },
  { cls: 'text-title-md', label: 'title-md 16/500' },
  { cls: 'text-title-sm', label: 'title-sm 14/500' },
  { cls: 'text-body-lg', label: 'body-lg 15/400' },
  { cls: 'text-body-md', label: 'body-md 14/400' },
  { cls: 'text-body-sm', label: 'body-sm 12/400' },
  { cls: 'text-label-md', label: 'label-md 12/500' },
  { cls: 'text-label-sm', label: 'label-sm 11/500' },
];

const SPACING = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'panel', 'gutter'];
const RADIUS = ['sm', 'md', 'lg', 'pill'];
const SHADOW = ['card', 'panel', 'popover'];

/** 폰트가 실제로 붙었나. ⚠️ 붙기 전에 찍으면 글리프가 깨져 보인다(CLAUDE.md). */
function useFontReady() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let alive = true;
    document.fonts.ready.then(() => {
      if (alive) setReady(document.fonts.check('16px SUIT'));
    });
    return () => {
      alive = false;
    };
  }, []);
  return ready;
}

export function TokenPreview() {
  const [dark, setDark] = useState(false);
  const fontReady = useFontReady();

  useEffect(() => {
    document.documentElement.dataset['theme'] = dark ? 'dark' : 'light';
  }, [dark]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-[1100px] px-gutter py-xxl">
        <header className="mb-xxl flex items-end justify-between gap-lg">
          <div>
            <h1 className="text-display-sm">LENS 토큰 점검</h1>
            <p className="mt-sm text-body-md text-muted-foreground">
              이식 1단계(뼈대). 값은 전부 <code>flutter_flow_theme.dart</code> 와{' '}
              <code>dsl/edit.dart</code> 에서 가져왔습니다 — 지어낸 값은 없습니다.
            </p>
          </div>
          <button
            onClick={() => setDark((v) => !v)}
            className="shrink-0 rounded-md border border-border bg-card px-lg py-sm text-title-sm shadow-card"
          >
            {dark ? '라이트로' : '다크로'}
          </button>
        </header>

        <Banner ready={fontReady} />

        <Section
          title="색"
          note="상태 셋(success·warning·error)이 lensStatusFor 의 good|warn|bad 와 짝입니다."
        >
          <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-md">
            {SWATCHES.map((s) => (
              <div
                key={s.label}
                className="overflow-hidden rounded-md border border-border bg-card shadow-card"
              >
                <div className={`h-14 ${s.cls}`} />
                <div className="px-md py-sm">
                  <div className="text-title-sm">{s.label}</div>
                  {s.note ? (
                    <div className="text-label-sm text-muted-foreground">{s.note}</div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section
          title="차트 5계열"
          note="이 순서가 범례 순서입니다. 바꾸면 모든 차트가 함께 바뀝니다."
        >
          <div className="flex gap-sm">
            {CHARTS.map((c, i) => (
              <div key={c} className="flex-1">
                <div
                  className="h-20 rounded-sm"
                  style={{ background: `var(--lens-${c})` }}
                />
                <div className="mt-sm text-label-md text-muted-foreground">{i + 1}</div>
              </div>
            ))}
          </div>
        </Section>

        <Section
          title="타이포"
          note="굵기 상한이 500 입니다 — SUIT 번들에 600/700 이 없어 위계는 크기와 색으로 만듭니다."
        >
          <div className="space-y-md">
            {TYPE.map((t) => (
              <div key={t.cls} className="flex items-baseline gap-lg">
                <span className={t.cls}>진행 불가 46.8% 완주율</span>
                <span className="text-label-sm text-muted-foreground">{t.label}</span>
              </div>
            ))}
          </div>
        </Section>

        <Section title="간격 · 반경 · 그림자">
          <div className="grid grid-cols-3 gap-xl">
            <div>
              <h3 className="mb-md text-title-sm">간격</h3>
              {SPACING.map((s) => (
                <div key={s} className="mb-sm flex items-center gap-md">
                  <div
                    className="h-3 bg-primary"
                    style={{ width: `var(--lens-space-${s})` }}
                  />
                  <span className="text-label-md text-muted-foreground">{s}</span>
                </div>
              ))}
            </div>
            <div>
              <h3 className="mb-md text-title-sm">반경</h3>
              {RADIUS.map((r) => (
                <div key={r} className="mb-sm flex items-center gap-md">
                  <div
                    className="h-10 w-16 border border-border bg-card"
                    style={{ borderRadius: `var(--lens-radius-${r})` }}
                  />
                  <span className="text-label-md text-muted-foreground">{r}</span>
                </div>
              ))}
            </div>
            <div>
              <h3 className="mb-md text-title-sm">그림자</h3>
              {SHADOW.map((s) => (
                <div key={s} className="mb-lg flex items-center gap-md">
                  <div
                    className="h-10 w-16 rounded-md bg-card"
                    style={{ boxShadow: `var(--lens-shadow-${s})` }}
                  />
                  <span className="text-label-md text-muted-foreground">{s}</span>
                </div>
              ))}
            </div>
          </div>
        </Section>

        <Section
          title="사이드바 면"
          note="라이트/다크 구분이 없습니다 — 원래 어두운 면이라 그렇습니다. 폭은 248px."
        >
          <div className="flex overflow-hidden rounded-lg shadow-panel">
            <div className="w-sidebar shrink-0 bg-sidebar p-panel">
              <div className="text-title-lg text-sidebar-foreground">LENS</div>
              <div className="mt-xl space-y-xs">
                <div className="rounded-sm bg-sidebar-accent px-md py-sm text-title-sm text-sidebar-accent-foreground">
                  개요
                </div>
                <div className="px-md py-sm text-title-sm text-sidebar-muted">완주율</div>
                <div className="px-md py-sm text-title-sm text-sidebar-muted">진행 불가</div>
              </div>
            </div>
            <div className="flex-1 bg-card p-panel text-body-md text-muted-foreground">
              콘텐츠 영역
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
}

function Banner({ ready }: { ready: boolean }) {
  return (
    <div
      className="mb-xxl rounded-md px-lg py-md text-body-md"
      style={{
        background: ready ? 'var(--lens-accent-success)' : 'var(--lens-accent-warning)',
      }}
    >
      {ready ? (
        <>
          SUIT 폰트가 붙었습니다. <span className="text-muted-foreground">
            document.fonts.check(&apos;16px SUIT&apos;) = true
          </span>
        </>
      ) : (
        <>
          SUIT 폰트가 아직 안 붙었습니다 — 이 상태로 찍은 화면으로 글리프를 판단하지 마십시오.
        </>
      )}
    </div>
  );
}

function Section({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-xxl">
      <h2 className="text-title-lg">{title}</h2>
      {note ? <p className="mt-xs mb-lg text-body-sm text-muted-foreground">{note}</p> : <div className="mb-lg" />}
      {children}
    </section>
  );
}
